/**
 * Middleware - Action Processing Pipeline
 * 
 * This module implements Redux-style middleware that can intercept,
 * modify, or react to actions before they reach the reducers.
 * Each middleware is a higher-order function that has access to 
 * dispatch and getState.
 * 
 * @module store/middleware
 */

import Config from '../core/Config.js';
import EventBus, { AppEvents } from '../core/EventBus.js';

/**
 * Logger Middleware
 * Logs all actions and state changes for debugging
 */
export const loggerMiddleware = (store) => (next) => (action) => {
  // Runtime kill-switch for verbose logs (helps avoid UI freezes)
  const DISABLE_VERBOSE = (typeof window !== 'undefined' && window.__DISABLE_VERBOSE_LOGS__ === true)
    || (typeof localStorage !== 'undefined' && localStorage.getItem('disableVerboseLogs') === 'true');
  // Skip logging for certain noisy actions in production
  const skipActions = ['SET_TYPING', 'SHOW_TOAST', 'HIDE_TOAST'];
  const shouldLog = !DISABLE_VERBOSE && (import.meta.env?.DEV || !skipActions.includes(action.type));

  if (shouldLog) {
    const prevState = store.getState();
    
    console.group(`🔄 Action: ${action.type}`);
    console.log('📥 Dispatched:', action);
    console.log('🕰️ Timestamp:', new Date().toISOString());
    console.log('📊 Previous State:', prevState);
    
    if (action.meta) {
      console.log('📋 Meta:', action.meta);
    }
  }

  // Call the next middleware/reducer
  const result = next(action);

  if (shouldLog) {
    const nextState = store.getState();
    console.log('📈 Next State:', nextState);
    
    // Show what changed
    const changes = findStateChanges(store.getState(), nextState);
    if (changes.length > 0) {
      console.log('🔄 Changes:', changes);
    }
    
    console.groupEnd();
  }

  return result;
};

/**
 * Persistence Middleware
 * Automatically saves state to localStorage based on action metadata
 */
export const persistenceMiddleware = (store) => (next) => (action) => {
  const result = next(action);

  // Check if action should trigger persistence
  if (action.meta?.persist) {
    setTimeout(() => {
      try {
        store.persistState();
        console.log('💾 State persisted after action:', action.type);
      } catch (error) {
        console.error('Failed to persist state:', error);
        store.dispatch({
          type: 'ADD_ERROR',
          payload: {
            message: 'Failed to save data',
            details: error.message,
            action: action.type
          }
        });
      }
    }, 100); // Small delay to batch multiple rapid actions
  }

  return result;
};

/**
 * Analytics Middleware
 * Tracks user actions for analytics and usage patterns
 */
export const analyticsMiddleware = (store) => (next) => (action) => {
  const result = next(action);

  // Only track in production and if analytics enabled
  const state = store.getState();
  const analyticsEnabled = state.user?.preferences?.enableAnalytics;

  if (analyticsEnabled && !import.meta.env?.DEV) {
    // Track significant user actions
    const trackableActions = [
      'ADD_INITIATIVE',
      'UPDATE_INITIATIVE',
      'DELETE_INITIATIVE',
      'SET_CURRENT_TAB',
      'ADD_CHAT_MESSAGE',
      'SET_API_KEY',
      'SET_UPLOADED_STRATEGY'
    ];

    if (trackableActions.includes(action.type)) {
      // Send to analytics service (implement as needed)
      console.log('📊 Analytics:', {
        action: action.type,
        timestamp: Date.now(),
        sessionId: state.system?.sessionId,
        version: state.system?.version
      });
    }
  }

  return result;
};

/**
 * Async Middleware (Thunk-style)
 * Allows action creators to return functions that get dispatch and getState
 */
export const asyncMiddleware = (store) => (next) => (action) => {
  // If action is a function, execute it with dispatch and getState
  if (typeof action === 'function') {
    return action(store.dispatch, store.getState);
  }

  return next(action);
};

/**
 * Validation Middleware
 * Validates actions before they reach reducers
 */
export const validationMiddleware = (store) => (next) => (action) => {
  // Validate action structure
  if (!action || typeof action !== 'object') {
    console.error('Invalid action: must be an object', action);
    return;
  }

  if (!action.type || typeof action.type !== 'string') {
    console.error('Invalid action: missing or invalid type', action);
    return;
  }

  // Validate sensitive actions
  if (action.meta?.sensitive && !action.meta.authorized) {
    console.warn('Sensitive action without authorization:', action.type);
  }

  // Check for required fields based on action type
  const validationRules = {
    'ADD_INITIATIVE': (action) => {
      if (!action.payload || !action.payload.title) {
        throw new Error('ADD_INITIATIVE requires payload.title');
      }
    },
    'UPDATE_INITIATIVE': (action) => {
      if (!action.payload || !action.payload.id) {
        throw new Error('UPDATE_INITIATIVE requires payload.id');
      }
    },
    'SET_API_KEY': (action) => {
      if (!action.payload || typeof action.payload !== 'string') {
        throw new Error('SET_API_KEY requires string payload');
      }
    }
  };

  const validator = validationRules[action.type];
  if (validator) {
    try {
      validator(action);
    } catch (error) {
      console.error(`Action validation failed for ${action.type}:`, error.message);
      store.dispatch({
        type: 'ADD_ERROR',
        payload: {
          message: 'Invalid action',
          details: error.message,
          action: action.type
        }
      });
      return;
    }
  }

  return next(action);
};

/**
 * Performance Middleware
 * Tracks action performance and warns about slow operations
 */
export const performanceMiddleware = (store) => (next) => (action) => {
  const start = performance.now();
  
  const result = next(action);
  
  const end = performance.now();
  const duration = end - start;

  // Warn about slow actions (>10ms)
  if (duration > 10) {
    console.warn(`⚠️ Slow action ${action.type}: ${duration.toFixed(2)}ms`);
  }

  // Track performance metrics
  if (import.meta.env?.DEV) {
    if (!window.actionPerformance) {
      window.actionPerformance = {};
    }
    
    if (!window.actionPerformance[action.type]) {
      window.actionPerformance[action.type] = [];
    }
    
    window.actionPerformance[action.type].push(duration);
    
    // Keep only last 100 measurements
    if (window.actionPerformance[action.type].length > 100) {
      window.actionPerformance[action.type].shift();
    }
  }

  return result;
};

/**
 * Event Bridge Middleware
 * Bridges Redux actions to EventBus for legacy compatibility
 */
export const eventBridgeMiddleware = (store) => (next) => (action) => {
  // Capture previous state BEFORE processing
  const prevState = store.getState();
  const result = next(action);
  const nextState = store.getState();

  // Emit events for backward compatibility with existing EventBus listeners
  const eventMap = {
    'SET_CURRENT_TAB': AppEvents.TAB_CHANGED,
    'ADD_INITIATIVE': AppEvents.INITIATIVE_ADDED,
    'UPDATE_INITIATIVE': AppEvents.INITIATIVE_UPDATED,
    'DELETE_INITIATIVE': AppEvents.INITIATIVE_DELETED,
    'ADD_CHAT_MESSAGE': AppEvents.CHAT_MESSAGE_ADDED,
    'SET_LOADING': AppEvents.LOADING_CHANGED,
    'APP_ERROR': AppEvents.ERROR_OCCURRED
  };

  const eventType = eventMap[action.type];
  if (eventType) {
    EventBus.emit(eventType, {
      action,
      state: nextState,
      payload: action.payload
    });
  }

  // Generic state change event is already emitted by AppState.
  // Keep this bridge emission behind a feature flag to avoid duplicates.
  if (Config?.FEATURES?.EVENT_BRIDGE_STATE === true) {
    EventBus.emit(AppEvents.STATE_CHANGED, {
      action,
      state: nextState,
      changes: findStateChanges(prevState, nextState)
    });
  }

  return result;
};

/**
 * Error Handling Middleware
 * Catches and handles errors in action processing
 */
export const errorMiddleware = (store) => (next) => (action) => {
  try {
    return next(action);
  } catch (error) {
    console.error(`Error processing action ${action.type}:`, error);
    
    // Dispatch error action
    store.dispatch({
      type: 'APP_ERROR',
      payload: {
        message: error.message,
        stack: error.stack,
        action: action.type,
        timestamp: new Date().toISOString()
      }
    });

    // Re-throw in development for debugging
    if (import.meta.env?.DEV) {
      throw error;
    }

    return undefined;
  }
};

/**
 * Debounce Middleware
 * Debounces rapid actions of the same type
 */
export const debounceMiddleware = (store) => {
  const debounceMap = new Map();
  
  return (next) => (action) => {
    // Actions to debounce
    const debounceActions = ['UPDATE_INITIATIVE', 'SET_TYPING'];
    const debounceDelay = 300; // ms

    if (debounceActions.includes(action.type)) {
      const key = `${action.type}:${JSON.stringify(action.payload)}`;
      
      // Clear existing timeout
      if (debounceMap.has(key)) {
        clearTimeout(debounceMap.get(key));
      }
      
      // Set new timeout
      const timeoutId = setTimeout(() => {
        debounceMap.delete(key);
        next(action);
      }, debounceDelay);
      
      debounceMap.set(key, timeoutId);
      
      return Promise.resolve(action);
    }

    return next(action);
  };
};

/**
 * DevTools Middleware
 * Integrates with Redux DevTools Extension
 */
export const devToolsMiddleware = (store) => (next) => (action) => {
  const result = next(action);

  // Send to Redux DevTools if available
  if (store.devTools) {
    store.devTools.send(action, store.getState());
  }

  return result;
};

/**
 * Compose Middleware
 * Combines multiple middleware into a single function
 */
export function composeMiddleware(middlewares) {
  return (store) => {
    const chain = middlewares.map(middleware => middleware(store));
    
    return chain.reduce((composed, middleware) => {
      return (next) => (action) => {
        return composed(middleware(next))(action);
      };
    });
  };
}

/**
 * Apply Middleware
 * Creates the middleware pipeline for a store
 */
export function applyMiddleware(...middlewares) {
  return (createStore) => (...args) => {
    const store = createStore(...args);
    
    let dispatch = () => {
      throw new Error('Dispatching while constructing your middleware is not allowed.');
    };

    const middlewareAPI = {
      getState: store.getState,
      dispatch: (...args) => dispatch(...args)
    };

    const chain = middlewares.map(middleware => middleware(middlewareAPI));
    dispatch = compose(...chain)(store.dispatch);

    return {
      ...store,
      dispatch
    };
  };
}

/**
 * Compose functions from right to left
 */
function compose(...funcs) {
  if (funcs.length === 0) {
    return (arg) => arg;
  }

  if (funcs.length === 1) {
    return funcs[0];
  }

  return funcs.reduce((a, b) => (...args) => a(b(...args)));
}

/**
 * Default Middleware Stack
 * The standard middleware configuration for the application
 */
export function createDefaultMiddleware() {
  // Minimal stack by default in DEV to avoid UI stalls
  const minimal = (typeof window !== 'undefined' && window.__MINIMAL_MIDDLEWARE__ === true)
    || (typeof localStorage !== 'undefined' && localStorage.getItem('minimalMiddleware') !== 'false')
    || import.meta.env?.DEV;

  if (minimal) {
    return [errorMiddleware, asyncMiddleware];
  }

  const middleware = [
    errorMiddleware,
    // Optional development aids
    ...(import.meta.env?.DEV ? [validationMiddleware, performanceMiddleware, loggerMiddleware] : []),
    // Core
    asyncMiddleware,
    debounceMiddleware,
    persistenceMiddleware,
    eventBridgeMiddleware,
    // Analytics opt-in
    ...(Config?.FEATURES?.ANALYTICS ? [analyticsMiddleware] : []),
    // DevTools last
    ...(import.meta.env?.DEV && typeof window !== 'undefined' && window.__REDUX_DEVTOOLS_EXTENSION__ ? [devToolsMiddleware] : [])
  ];
  return middleware;
}

/**
 * Utility Functions
 */

/**
 * Find differences between two state objects
 */
function findStateChanges(prevState, nextState, path = '') {
  const changes = [];

  // Handle null/undefined states
  if (!prevState || !nextState) {
    return path ? [path] : ['root'];
  }

  for (const key in nextState) {
    const currentPath = path ? `${path}.${key}` : key;
    const prevValue = prevState[key];
    const nextValue = nextState[key];

    if (typeof nextValue === 'object' && nextValue !== null && !Array.isArray(nextValue)) {
      // Recursively check nested objects
      changes.push(...findStateChanges(prevValue || {}, nextValue, currentPath));
    } else if (prevValue !== nextValue) {
      changes.push(currentPath);
    }
  }

  return changes;
}

/**
 * Create action metadata enhancer
 */
export function enhanceAction(action, enhancements = {}) {
  return {
    ...action,
    meta: {
      ...action.meta,
      ...enhancements,
      timestamp: Date.now(),
      id: Math.random().toString(36).substr(2, 9)
    }
  };
}

/**
 * Create conditional middleware
 */
export function conditionalMiddleware(condition, middleware) {
  return (store) => (next) => (action) => {
    if (condition(action, store.getState())) {
      return middleware(store)(next)(action);
    }
    return next(action);
  };
}

// Export for debugging in development
if (import.meta.env?.DEV) {
  window.StoreMiddleware = {
    loggerMiddleware,
    persistenceMiddleware,
    analyticsMiddleware,
    asyncMiddleware,
    validationMiddleware,
    performanceMiddleware,
    eventBridgeMiddleware,
    errorMiddleware,
    debounceMiddleware,
    devToolsMiddleware,
    composeMiddleware,
    applyMiddleware,
    createDefaultMiddleware,
    enhanceAction,
    conditionalMiddleware
  };
  console.log('🔧 Store middleware available at window.StoreMiddleware');
}
